import { ReactNode } from 'react';
import { useTutorialContext } from 'context/TutorialContext';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { StepProps } from '@components/BookingProcessModal/common';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import { UserTutorialCommandApi } from '@lib/api/endpoints/UserTutorial';
import { completeTutorial } from '@lib/redux.lib/slices/appSlice';
import { RootState } from '@lib/redux.lib/store';

import { stepsList } from '../steps/stepsList';

type StepsSkeletonProps = StepProps & {
  children: ReactNode;
};
export const StepsSkeleton = ({
  goToNextStep,
  goToPreviousStep,
  currentStepIndex,
  children,
}: StepsSkeletonProps) => {
  return (
    <StyledContainer>
      <div className='skeleton-content'>{children}</div>

      <ActionBar
        goToNextStep={goToNextStep}
        goToPreviousStep={goToPreviousStep}
        currentStepIndex={currentStepIndex}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - 300px);
  font-size: 14px;
  line-height: 20px;

  div.skeleton-content {
    flex-grow: 1;
  }

  @media (max-width: 960px) {
    width: auto;
    div.skeleton-content {
      flex-grow: 0;
    }
  }
`;

const ActionBar = ({
  goToNextStep,
  currentStepIndex,
  goToPreviousStep,
}: StepProps) => {
  const appDispatch = useDispatch();
  const { bookingProcessModalVisibility } = useTutorialContext();

  const userId = useSelector((state: RootState) => state.app.user.profile?.id);
  const { label: primaryActionLabel, action: primaryAction } =
    currentStepIndex === stepsList.length - 1
      ? {
          label: 'All Done',
          action: () => {
            bookingProcessModalVisibility.hideModal();
            if (!userId) return;
            UserTutorialCommandApi.upsertUserTutorial({
              tutorial_name: 'booking_process',
              is_completed: true,
              user_id: userId,
            }).then(() => {
              appDispatch(completeTutorial('booking_process'));
            });
          },
        }
      : { label: 'Next', action: goToNextStep };
  return (
    <StyledActionBar>
      {currentStepIndex > 0 && (
        <StyledSecondaryButton onClick={goToPreviousStep}>
          Back
        </StyledSecondaryButton>
      )}

      <StyledPrimaryButton onClick={primaryAction}>
        {primaryActionLabel}
      </StyledPrimaryButton>
    </StyledActionBar>
  );
};

const StyledActionBar = styled.div`
  display: flex;
  justify-content: end;
  gap: 8px;
  padding: 20px;
  border-top: 1px solid ${({ theme }) => theme.palette.stroke[0]};

  @media (max-width: 960px) {
    border-top: none;
    padding: 0 20px 20px;
    font-size: 16px;
    line-height: 24px;
    gap: 12px;
    justify-content: center;
  }
`;

const StyledButton = styled(UnstyledButton)`
  width: 82px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  border-radius: 4px;

  @media (max-width: 960px) {
    width: auto;
    height: 48px;
    flex: 1;
  }
`;

const StyledPrimaryButton = styled(StyledButton)`
  color: #fff;

  background-color: ${({ theme }) => theme.palette.primary[0]};

  @media (max-width: 960px) {
    border: 1px solid ${({ theme }) => theme.palette.primary[0]};
  }
`;

const StyledSecondaryButton = styled(StyledButton)`
  @media (max-width: 960px) {
    border: 1px solid ${({ theme }) => theme.palette.stroke[0]};
  }
`;
